// @refresh reset
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  useCallback,
} from 'react';

const isDev = import.meta.env.DEV;

// Define the shape of the context
interface LanguageContextType {
  language: string;
  setLanguage: (language: string) => void;
  t: (key: string, options?: Record<string, string>) => string;
}

// Create the context with a default value
export const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

// Create the provider component
export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState(
    localStorage.getItem('language') || 'en'
  );
  const [translations, setTranslations] = useState<Record<string, string>>({});

  const fetchTranslations = useCallback(async (lang: string, retryCount = 0) => {
    if (isDev) console.log(`[i18n] Fetching translations for: ${lang} (attempt: ${retryCount + 1})`);
    try {
      const response = await fetch(`/translations/${lang}.json`);
      if (!response.ok) {
        if (isDev) console.error(`[i18n] Could not load translation file for ${lang} (status: ${response.status})`);
        // Fallback to English if the desired language file is not found
        if (lang !== 'en') {
          if (isDev) console.log('[i18n] Falling back to English translations');
          fetchTranslations('en');
        }
        return;
      }
      const data = await response.json();
      if (isDev) console.log(`[i18n] Successfully loaded ${Object.keys(data).length} translations for ${lang}`);
      setTranslations(data);
    } catch (error) {
      if (isDev) console.error('[i18n] Failed to fetch translations:', error);

      // Retry logic for network errors
      if (retryCount < 2) {
        if (isDev) console.log(`[i18n] Retrying translation fetch for ${lang} in 1 second...`);
        setTimeout(() => fetchTranslations(lang, retryCount + 1), 1000);
      } else if (lang !== 'en') {
        if (isDev) console.log('[i18n] Max retries reached, falling back to English');
        fetchTranslations('en');
      }
    }
  }, []);

  useEffect(() => {
    fetchTranslations(language);
  }, [language, fetchTranslations]);

  const setLanguage = (lang: string) => {
    localStorage.setItem('language', lang);
    setLanguageState(lang);
  };

  const t = useCallback(
    (key: string, options?: Record<string, string>) => {
      let translation = translations[key];
      if (!translation) {
        if (isDev) console.warn(`[i18n] Missing translation for key: '${key}' in language: '${language}'. Available keys: ${Object.keys(translations).length}`);
        
        // Show a loading indicator instead of raw key if translations haven't loaded yet
        if (Object.keys(translations).length === 0) {
          return '...'; // Loading placeholder
        }
        
        // Fallback to key without namespace if full key doesn't exist
        const fallbackKey = key.split('.').pop() || key;
        translation = translations[fallbackKey] || key;
      }
      
      if (options) {
        Object.keys(options).forEach((k) => {
          translation = translation.replace(`{{${k}}}`, options[k]);
        });
      }
      return translation;
    },
    [translations, language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

